import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {CLDispatch, RootState} from 'common/src/redux/store';
import getActiveComp from '../agents/adBuilderAgent/utils/getActiveComp';
import getSelectedLayerIndexes from '../agents/adBuilderAgent/utils/getSelectedLayerIndexes';

interface AdBuilderContext {
  currentComp?: {
    id: string;
    width: number;
    height: number;
    duration: number;
    fps: number;
    layers: Array<{
      i: number;
      id: string;
      name: string;
      type: string;
    }>;
    selectedLayerIndexes: number[];
  };
}

export default function adBuilderContext(_store: {
  getState: () => RootState;
  dispatch: CLDispatch;
}): AgentContext<AdBuilderContext> {
  return {
    title: 'Current Composition Details',
    getter: (): AdBuilderContext => {
      const activeComp = getActiveComp(_store);
      const selectedLayerIndexes = getSelectedLayerIndexes(_store);

      if (!activeComp) {
        return {currentComp: undefined};
      }

      try {
        // Build current comp information with layers
        const currentComp = activeComp
          ? {
              id: activeComp.id,
              width: activeComp.w,
              height: activeComp.h,
              duration: activeComp.dur,
              fps: activeComp.fps,
              layers: Object.values(activeComp.l).map((layer) => {
                return {
                  id: layer.id,
                  name: layer.name,
                  type: layer.ty,
                  i: parseInt(layer.i),
                };
              }),
              selectedLayerIndexes: selectedLayerIndexes || [],
            }
          : undefined;

        return {currentComp};
      } catch (error) {
        return {currentComp: undefined};
      }
    },

    formatter: ({currentContext, prevContext}) => {
      if (!currentContext) {
        return null;
      }

      if (!currentContext.currentComp) {
        return 'No active composition is currently available.';
      }

      const {currentComp} = currentContext;
      const prevComp = prevContext?.currentComp;

      // ============================================================================
      // SECTION 1: COMPOSITION DETAILS
      // ============================================================================
      const compChanged =
        !prevComp ||
        prevComp.id !== currentComp.id ||
        prevComp.width !== currentComp.width ||
        prevComp.height !== currentComp.height ||
        prevComp.duration !== currentComp.duration ||
        prevComp.fps !== currentComp.fps;

      const compInfo = compChanged
        ? `Composition ID: ${currentComp.id}, Size: ${currentComp.width} x ${currentComp.height} px, Duration: ${currentComp.duration} Sec, Frame Rate: ${currentComp.fps}`
        : '';

      // ============================================================================
      // SECTION 2: LAYERS MANAGEMENT
      // ============================================================================
      const prevLayerIds = new Set(prevComp?.layers.map((layer) => layer.id) || []);
      const currentLayerIds = new Set(currentComp.layers.map((layer) => layer.id));

      // Check for layer changes (added or removed)
      const layersChanged =
        prevLayerIds.size !== currentLayerIds.size ||
        !Array.from(prevLayerIds).every((id) => currentLayerIds.has(id));

      const layersInfo = layersChanged
        ? currentComp.layers.length > 0
          ? `\n\nLayers (${currentComp.layers.length} total, index is zOrder):\n` +
            currentComp.layers
              .slice()
              .reverse()
              .map((layer) => {
                // Only mark as [ADDED] if there was a previous composition and the layer is new
                const isNewLayer = prevComp && !prevLayerIds.has(layer.id);
                const suffix = isNewLayer ? ' [ADDED]' : '';

                return `${layer.i}. ${layer.name} (ID: ${layer.id}, Type: ${layer.type})${suffix}`;
              })
              .join('\n')
          : '\n\nNo layers in the composition.'
        : '';

      // ============================================================================
      // SECTION 3: DELETED LAYERS
      // ============================================================================
      const deletedLayerIds = Array.from(prevLayerIds).filter((id) => !currentLayerIds.has(id));
      const deletedLayersInfo =
        deletedLayerIds.length > 0 ? `\n\nDeleted layers IDs: ${deletedLayerIds.join(', ')}` : '';

      // ============================================================================
      // SECTION 4: SELECTED LAYERS
      // ============================================================================
      const prevSelectedIndexes = prevComp?.selectedLayerIndexes || [];
      const currentSelectedIndexes = currentComp.selectedLayerIndexes;
      const selectedLayersChanged =
        prevSelectedIndexes.length !== currentSelectedIndexes.length ||
        !prevSelectedIndexes.every((index) => currentSelectedIndexes.includes(index));

      const selectedLayersInfo = selectedLayersChanged
        ? currentSelectedIndexes.length > 0
          ? `\n\nSelected Layer Indexes: ${currentSelectedIndexes.join(', ')}`
          : '\n\nNo Selected Layers'
        : '';

      // ============================================================================
      // SECTION 5: ASSEMBLE FINAL CONTEXT
      // ============================================================================
      return compInfo + layersInfo + selectedLayersInfo + deletedLayersInfo;
    },
  };
}
